<template>
  <div v-if="showModal" class="motd-overlay" @click="dismissMotd(false)">
    <div class="motd-container" @click.stop="dismissMotd(true)">
      <div class="motd-title">Message of the Day</div>
      <div class="motd-content">
        <div class="motd-body" v-html="renderedContent"></div>
        <div class="motd-footer">
          tap this message to dismiss.<br />
          Only displays twice.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { marked } from 'marked';
import axios from '@/axiosConfig';

export default {
  name: 'MotdModal',
  props: {
    route: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const showModal = ref(false);
    const motdContent = ref('');
    const motdId = ref(null);

    // Use computed property to render markdown content
    const renderedContent = computed(() => {
      if (!motdContent.value) return '';
      return marked.parse(motdContent.value);
    });

    // Function to check and display MOTD
    const checkMotd = async () => {
      try {
        // Get the latest MOTD for the current route
        const response = await axios.get('/api/motd', {
          params: { route: props.route },
        });

        if (response.status === 200 && response.data.length !== 0) {
          const { id, motd } = response.data;
          motdContent.value = motd;
          motdId.value = id;

          // Get stored MOTD data from localStorage
          const storedMotdDataString =
            localStorage.getItem('motd_data') || '{}';
          const storedMotdData = JSON.parse(storedMotdDataString);

          // Check if we have data for this specific route and motd id
          const routeKey = `${props.route}_${id}`;
          const motdInfo = storedMotdData[routeKey] || { count: 0 };

          // Only show if it has been viewed less than twice
          if (motdInfo.count < 2) {
            showModal.value = true;
          }
        }
      } catch (error) {
        console.error('Error fetching MOTD:', error);
      }
    };

    // Dismiss the modal when clicked
    const dismissMotd = (incrementCount = true) => {
      if (showModal.value && motdId.value) {
        // Only update the count if incrementCount is true (user clicked on content, not overlay)
        if (incrementCount) {
          // Get stored MOTD data
          const storedMotdDataString =
            localStorage.getItem('motd_data') || '{}';
          const storedMotdData = JSON.parse(storedMotdDataString);

          // Create or update the entry for this route and motd
          const routeKey = `${props.route}_${motdId.value}`;
          const motdInfo = storedMotdData[routeKey] || { count: 0 };

          // Increment the count and update the object
          motdInfo.count += 1;
          storedMotdData[routeKey] = {
            route: props.route,
            motdId: motdId.value,
            count: motdInfo.count,
          };

          // Save back to localStorage
          localStorage.setItem('motd_data', JSON.stringify(storedMotdData));
        }
      }

      showModal.value = false;
    };

    onMounted(() => {
      checkMotd();
    });

    return {
      showModal,
      renderedContent,
      dismissMotd,
    };
  },
};
</script>

<style scoped>
.motd-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000000;
}

.motd-container {
  width: 75%;
  max-width: 420px;
  max-height: 50%;
  background-color: #fff;
  border-radius: 5px;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.motd-title {
  font-size: 16pt;
  width: 100%;
  text-align: center;
  color: white;
  background: #675769;
  padding: 10px 0;
  flex-shrink: 0;
}

.motd-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 25px;
  position: relative;
  overflow-y: auto;
  color: #333;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.motd-content::-webkit-scrollbar {
  display: none;
}

.motd-content :deep(li) {
  margin-bottom: 8px;
}

.motd-body {
  flex: 1;
}

.motd-footer {
  font-size: 0.8em;
  color: #999;
  text-align: center;
  padding-bottom: 5px;
}
</style>
